import type { ClientKind, Session } from "@baton/contracts";
import type { DecryptedMessage } from "../messages.js";

export interface ThreadMessage extends DecryptedMessage {
  /** Optimistic entry that has not been confirmed by the API yet. */
  pending?: boolean;
  failed?: boolean;
}

/** Every message in a project exchanged between two agents, oldest first, deduplicated by id. */
export function selectThread(
  all: Iterable<ThreadMessage>,
  projectId: string,
  agentA: string,
  agentB: string,
): ThreadMessage[] {
  const seen = new Set<string>();
  const thread: ThreadMessage[] = [];
  for (const message of all) {
    if (message.projectId !== projectId || seen.has(message.id)) continue;
    const between = (message.senderAgentId === agentA && message.recipientAgentIds.includes(agentB))
      || (message.senderAgentId === agentB && message.recipientAgentIds.includes(agentA));
    if (!between) continue;
    seen.add(message.id);
    thread.push(message);
  }
  return thread.sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id));
}

export interface Presence {
  online: boolean;
  client: ClientKind | null;
  heartbeatAt: string | null;
}

/** "Online" means a non-dashboard session bound to the agent heartbeated inside the window. */
export function presenceFor(agentId: string, sessions: readonly Session[], now: number, windowMs = 90_000): Presence {
  const bound = sessions
    .filter((session) => session.boundAgentId === agentId && session.client !== "dashboard")
    .sort((left, right) => right.heartbeatAt.localeCompare(left.heartbeatAt));
  const latest = bound[0];
  if (!latest) return { online: false, client: null, heartbeatAt: null };
  const age = now - Date.parse(latest.heartbeatAt);
  return { online: age >= 0 && age < windowMs, client: latest.client, heartbeatAt: latest.heartbeatAt };
}

/** Compact recipient state for chips: the state plus the decrypted completion outcome when present. */
export function summarizeRecipientStates(message: ThreadMessage): Array<{ agentId: string; state: string; outcome: string | null }> {
  return message.recipientStates.map((recipient) => ({
    agentId: recipient.agentId,
    state: recipient.state,
    outcome: recipient.result?.outcome ?? null,
  }));
}
